import { apiRequest } from 'api/apiClient';
import {
  buildPageModuleContentPayload,
  buildPageModuleName,
  buildPageModuleType,
} from '../utils/pageModulePayload';

// Location Groups
const createOrUpdateLocationGroup = async (locationGroupData) => {
  if (locationGroupData.uid) {
    const { uid, ...data } = locationGroupData;
    const response = await apiRequest('PATCH', `/v1/location-groups/${uid}`, data);
    return response;
  }
  const response = await apiRequest('POST', '/v1/location-groups', locationGroupData);
  return response;
};

const deleteLocationGroup = async (uid) => {
  const response = await apiRequest('DELETE', `/v1/location-groups/${uid}`);
  return response;
};

// Locations

const createLocation = async ({
  name,
  description,
  regionUid,
  vanityUrl,
  location_tracking_id,
  inviteOwnerEmail,
}) => {
  const response = await apiRequest('POST', '/v1/locations', {
    name,
    description,
    region_uid: regionUid,
    vanity_url: vanityUrl,
    location_tracking_id,
    ...(inviteOwnerEmail ? { owner_email: inviteOwnerEmail } : {}),
  });
  return response;
};

const updateLocation = async ({ uid, content = {} }) => {
  const locationContent = { ...content };
  delete locationContent.address;
  const response = await apiRequest('PATCH', `/v1/locations/${uid}`, locationContent);
  return response;
};

const updateLocationAddress = async ({ uid, address }) => {
  const response = await apiRequest('PATCH', `/v1/locations/${uid}/address`, address);
  return response;
};

const updateLocationOpeningHours = async ({ uid, openingHours }) => {
  const response = await apiRequest('PATCH', `/v1/locations/${uid}/opening-hours`, openingHours);
  return response;
};

const deleteLocation = async (locationUid) => {
  const response = await apiRequest('DELETE', `/v1/locations/${locationUid}`);
  return response;
};

// Location Participants
const addLocationParticipant = async ({ locationUid, content }) => {
  const { adminUid, retailerUid, role } = content;

  const payload = {
    location: locationUid,
    role,
  };

  if (adminUid) {
    payload.admin_uid = adminUid;
  } else if (retailerUid) {
    payload.retailer_uid = retailerUid;
  }

  const response = await apiRequest('POST', '/v1/location-assignments', payload);
  return response;
};

const removeLocationParticipant = async (participantUid) => {
  const response = await apiRequest('DELETE', `/v1/location-assignments/${participantUid}`);
  return response;
};

const updateLocationParticipant = async ({ participantUid, content }) => {
  const { role } = content;
  const response = await apiRequest('PATCH', `/v1/location-assignments/${participantUid}`, {
    role,
  });
  return response;
};

// Location Modules
const addLocationModule = async ({ locationUid, moduleType, name, content }) => {
  const transformedContent = buildPageModuleContentPayload(moduleType, content);
  const moduleName = buildPageModuleName(moduleType, name);
  const apiModuleType = buildPageModuleType(moduleType);

  const response = await apiRequest('POST', `/v1/locations/${locationUid}/modules`, {
    module_type: apiModuleType,
    name: moduleName,
    ...transformedContent,
  });
  return response;
};

const updateLocationModule = async ({ locationUid, moduleUid, moduleType, name, content }) => {
  const moduleName = buildPageModuleName(moduleType, name);
  const transformedContent =
    content === undefined ? {} : buildPageModuleContentPayload(moduleType, content);

  const response = await apiRequest('PATCH', `/v1/locations/${locationUid}/modules/${moduleUid}`, {
    name: moduleName,
    ...transformedContent,
  });
  return response;
};

const moveLocationModule = async ({ locationUid, uid, before, after }) => {
  const response = await apiRequest('PATCH', `/v1/locations/${locationUid}/modules/${uid}/move`, {
    ...(before ? { before } : {}),
    ...(after ? { after } : {}),
  });
  return response;
};

const deleteLocationModule = async ({ locationUid, moduleUid }) => {
  const response = await apiRequest('DELETE', `/v1/locations/${locationUid}/modules/${moduleUid}`);
  return response;
};

const LOCATIONS_MUTATIONS = {
  // Location Groups
  CREATE_OR_UPDATE_LOCATION_GROUP: createOrUpdateLocationGroup,
  DELETE_LOCATION_GROUP: deleteLocationGroup,

  // Locations
  CREATE_LOCATION: createLocation,
  UPDATE_LOCATION: updateLocation,
  UPDATE_LOCATION_ADDRESS: updateLocationAddress,
  UPDATE_LOCATION_OPENING_HOURS: updateLocationOpeningHours,
  DELETE_LOCATION: deleteLocation,

  // Location Participants
  ADD_LOCATION_PARTICIPANT: addLocationParticipant,
  REMOVE_LOCATION_PARTICIPANT: removeLocationParticipant,
  UPDATE_LOCATION_PARTICIPANT: updateLocationParticipant,

  // Location Modules
  ADD_LOCATION_MODULE: addLocationModule,
  UPDATE_LOCATION_MODULE: updateLocationModule,
  MOVE_LOCATION_MODULE: moveLocationModule,
  DELETE_LOCATION_MODULE: deleteLocationModule,
};

export default LOCATIONS_MUTATIONS;
